<template>
  <main class="main">
    <div class="hero-section-container">
      <img src="../assets/bg_hero.svg" class="img"/>
      <div class="hero-blur-overlay"></div>
      <div class="section hero-section">
        <div class="wrapper">
          <div class="title Ruststation">
            Ruststation
          </div>
          <div class="hero-character-banner">
            <img src="../images/Group 52.png" alt="Character Banner" class="character-banner-img"/>
            <div class="banner-red-circle"></div>
          </div>
          <div class="server-status">
            {{ $t('Server Status:') }} <span class="online">{{ $t('ONLINE') }}</span> - {{ $t('jump in now!') }}
          </div>
        </div>
      </div>
    </div>
    <div class="section sales-section">
      <div class="wrapper">
        <div class="sales-header">
          <h2 class="sales-title">{{ $t('Sales') }}</h2>
          <a href="/Products" class="see-all-link">{{ $t('see all') }}</a>
        </div>
        <div class="sales-carousel">
          <button class="carousel-nav carousel-prev" data-section="sales">
            <img src="../assets/levo.svg" alt="Previous" />
          </button>
          <div class="products-container" v-if="!salesLoading">
            <div 
              class="product-card" 
              v-for="product in salesProducts" 
              :key="'sales-' + product.id"
              @click="openProductModal(product)"
            >
                <div class="product-image">
                  <img 
                    :src="getProductImage(product)" 
                    :alt="product.name"
                    @error="handleImageError"
                  />
                </div>
                <div class="product-bottom">
                  <div class="product-name">{{ product.name }}</div>
                  <div class="product-prices">
                    <div 
                      class="product-original-price" 
                      v-if="product.old_price && product.old_price > product.price"
                    >
                      {{ currency }} {{ product.old_price.toFixed(2) }}
                    </div>
                    <div class="product-price">
                      {{ currency }} {{ product.price.toFixed(2) }}
                    </div>
                  </div>
                </div>
            </div>
          </div>
          <div class="loading-container" v-if="salesLoading">
            <div class="loading-spinner">{{ $t('Loading...') }}</div>
          </div>
          <div class="error-container" v-if="salesError && !salesLoading">
            <div class="error-message">{{ salesError }}</div>
          </div>
          <button class="carousel-nav carousel-next" data-section="sales">
            <img src="../assets/levo.svg" alt="Next" />
          </button>
        </div>
      </div>
    </div>
    <div class="section welcome-section">
      <div class="welcome-character">
        <img src="../images/welcome_image_left.svg" alt="Character" class="character-img"/>
      </div>
      <div class="wrapper">
        <div class="welcome-content">
          <div class="welcome-left">
            <div class="welcome-text">
              <h2 class="welcome-title">{{ $t('Welcome to') }} <span class="project-name">{{ $t('Ruststation') }}</span></h2>
              <p class="welcome-subtitle">{{ $t('Your one-stop survival arena for Rust.') }}</p>
              <p class="welcome-description">
                {{ $t('Face the unforgiving wilderness, craft your path, and clash with rivals—free of charge. Need an edge? Browse our optional in-game goodies to spice up every skirmish. Dive into a living world where each choice could be your last—or your greatest victory.') }}
              </p>
            </div>
          </div>
          <div class="welcome-right">
            <div class="image-gallery">
              <div class="gallery-single">
                <img src="../images/welcome_section.png" alt="Welcome Section" class="gallery-img"/>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="section rules-section">
      <div class="wrapper">
        <div class="sales-header">
          <h2 class="sales-title">{{ $t('Bestsellers') }}</h2>
          <a href="/Products" class="see-all-link">{{ $t('see all') }}</a>
        </div>
        <div class="sales-carousel">
          <button class="carousel-nav carousel-prev" data-section="bestsellers">
            <img src="../assets/levo.svg" alt="Previous" />
          </button>
          <div class="products-container" v-if="!bestsellersLoading">
            <div 
              class="product-card" 
              v-for="product in bestsellersProducts" 
              :key="'bestsellers-' + product.id"
              @click="openProductModal(product)"
            >
                <div class="product-image">
                  <img 
                    :src="getProductImage(product)" 
                    :alt="product.name"
                    @error="handleImageError"
                  />
                </div>
                <div class="product-bottom">
                  <div class="product-name">{{ product.name }}</div>
                  <div class="product-prices">
                    <div class="product-price">
                      {{ currency }} {{ product.price.toFixed(2) }}
                    </div>
                  </div>
                </div>
            </div>
          </div>
          <div class="loading-container" v-if="bestsellersLoading">
            <div class="loading-spinner">{{ $t('Loading...') }}</div>
          </div>
          <div class="error-container" v-if="bestsellersError && !bestsellersLoading">
            <div class="error-message">{{ bestsellersError }}</div>
          </div>
          <button class="carousel-nav carousel-next" data-section="bestsellers">
            <img src="../assets/levo.svg" alt="Next" />
          </button>
        </div>
      </div>
    </div>

    <div class="section armory-section">
      <div class="wrapper">
        <h2 class="armory-title">{{ $t('The Armory & Supply Depot') }}</h2>
        <div class="armory-features">
          <div class="armory-feature">
            <div class="armory-icon">
              <img src="../images/discount.svg" alt="Discount" />
            </div>
            <div class="armory-text">
              {{ $t('New stock lands daily, so check back often to stay ahead of the competition.') }}
            </div>
          </div>
          <div class="armory-feature">
            <div class="armory-icon">
              <img src="../images/backpac.svg" alt="Backpack" />
            </div>
            <div class="armory-text">
              {{ $t('Sort prices, search by name, and fill your pack with everything from Spider Webs to HV 5.56.') }}
            </div>
          </div>
          <div class="armory-feature">
            <div class="armory-icon">
              <img src="../images/armory.svg" alt="Armory" />
            </div>
            <div class="armory-text">
              {{ $t('Filter by category—Weapons, Ammo, Resources, Tools, Medicine, Apparel, and more.') }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="section server-specs-section">
      <div class="server-specs-image">
        <img src="../assets/SSP.png" alt="Server Specs Characters" class="ssp-characters" />
      </div>
      <div class="wrapper">
        <div class="server-specs-title">{{ $t('Server Specs & Performance') }}</div>
        <div class="server-specs-cards">
          <div 
            class="spec-card" 
            :class="{ active: activeCard === 0 }"
            @click="toggleCard(0)"
          >
            <h3 class="spec-card-title">{{ $t('Dedicated Hardware') }}</h3>
            <div class="spec-card-content" v-if="activeCard === 0">
              <p class="spec-card-description">
                {{ $t('Top-tier CPUs and NVMe SSDs ensure smooth gameplay even during massive firefights.') }}
              </p>
            </div>
          </div>
          <div 
            class="spec-card" 
            :class="{ active: activeCard === 1 }"
            @click="toggleCard(1)"
          >
            <h3 class="spec-card-title">{{ $t('Global Datacenters') }}</h3>
            <div class="spec-card-content" v-if="activeCard === 1">
              <p class="spec-card-description">
                {{ $t('Strategically located nodes cut latency for players in Europe, North America, and Asia.') }}
              </p>
            </div>
          </div>
          <div 
            class="spec-card" 
            :class="{ active: activeCard === 2 }"
            @click="toggleCard(2)"
          >
            <h3 class="spec-card-title">{{ $t('99.9% Uptime SLA') }}</h3>
            <div class="spec-card-content" v-if="activeCard === 2">
              <p class="spec-card-description">
                {{ $t('Top-tier CPUs and NVMe SSDs ensure smooth gameplay even during massive firefights.') }}
              </p>
            </div>
          </div>
          <div 
            class="spec-card" 
            :class="{ active: activeCard === 3 }"
            @click="toggleCard(3)"
          >
            <h3 class="spec-card-title">{{ $t('Automated Backups') }}</h3>
            <div class="spec-card-content" v-if="activeCard === 3">
              <p class="spec-card-description">
                {{ $t('Hourly snapshots protect your progress from crashes or rollbacks.') }}
              </p>
            </div>
          </div>
          <div 
            class="spec-card" 
            :class="{ active: activeCard === 4 }"
            @click="toggleCard(4)"
          >
            <h3 class="spec-card-title">{{ $t('Advanced Anti-Cheat') }}</h3>
            <div class="spec-card-content" v-if="activeCard === 4">
              <p class="spec-card-description">
                {{ $t('Real-time detection plus manual review keeps the battlefield fair.') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div id="faq" class="section faq-section">
      <!-- Top Character Image -->
      <div class="faq-top-character">
        <img src="../assets/faqup.png" alt="FAQ Top Character" />
      </div>
      
      <div class="wrapper">
        <h2 class="faq-title">{{ $t('FAQ') }}</h2>
        <div class="faq-images">
          <div class="faq-item">
            <img src="../assets/faq1.png" alt="FAQ 1" class="faq-image" />
            <div class="faq-text">{{ $t('IS THE SERVER FREE?') }}</div>
            <div class="faq-hover-text">{{ $t('Absolutely. Play at no cost; cosmetic and utility items are optional extras.') }}</div>
          </div>
          <div class="faq-item">
            <img src="../assets/faq2.png" alt="FAQ 2" class="faq-image" />
            <div class="faq-text">{{ $t('HOW DO I REACH YOU?') }}</div>
            <div class="faq-hover-text">
              {{ $t('faq_contact_text', { email: supportEmail }) }}
            </div>
          </div>
          <div class="faq-item">
            <img src="../assets/faq3.png" alt="FAQ 3" class="faq-image" />
            <div class="faq-text">
              <div class="faq-question">{{ $t('CONNECTING TO THE SERVER?') }}</div>
            </div>
            <div class="faq-hover-text">
              {{ $t('faq_server_text', { ip: serverIp, port: serverPort }) }}
            </div>
          </div>
          <div class="faq-item">
            <img src="../assets/faq4.png" alt="FAQ 4" class="faq-image" />
            <div class="faq-text">{{ $t('HOW DO I PURCHASE ITEMS?') }}</div>
            <div class="faq-hover-text">{{ $t('Sign in with Steam, top up your balance, and grab the gear you like—done.') }}</div>
          </div>
        </div>
      </div>
      
      <!-- Bottom Blur Image -->
      <div class="faq-bottom-blur">
        <img src="../assets/faqblur.png" alt="FAQ Bottom Blur" />
      </div>
    </div>
  </main>
</template>
<script>

export default {
  name: 'Home',
  props: ['currency', 'currencyCode'],
  components: {
  },
  data: function() {
    return {
      currentSalesPage: 1,
      currentBestsellersPage: 1,
      salesProducts: [],
      bestsellersProducts: [],
      salesLoading: true,
      bestsellersLoading: true,
      salesError: null,
      bestsellersError: null,
      imgDomain: process.env.VUE_APP_STATIC_DOMAIN,
      activeCard: null
    }
  },
   computed: {
    supportEmail() {
      return this.$parent.settings?.support_email || 'EMAIL';
    },
    serverIp() {
      return this.$parent.ip || 'IP';
    },
    serverPort() {
      return this.$parent.port || 'PORT';
    }
  },
  watch: {
    currencyCode() {
      this.getSalesProducts();
      this.getBestsellersProducts();
    }
  },
  mounted() {
    this.getSalesProducts();
    this.getBestsellersProducts();
    this.initCarousel();
  },
  methods: {
    async getSalesProducts(page = 1) {
      this.salesLoading = true;
      this.salesError = null;
      
      try {
        const response = await this.$http.get(process.env.VUE_APP_API + `items?per_page=20&page=${page}&sort=price`);
        const allProducts = response.data.data || response.data.payload || response.data;
        
        const salesItems = allProducts.filter(product => 
          product.old_price && product.old_price > product.price
        );
        
        this.salesProducts = salesItems.slice(0, 5);
        
        if (this.salesProducts.length < 5) {
          const regularProducts = allProducts.filter(product => 
            !product.old_price || product.old_price <= product.price
          ).slice(0, 5 - this.salesProducts.length);
          this.salesProducts = [...this.salesProducts, ...regularProducts];
        }
        
        this.salesLoading = false;
      } catch (error) {
        console.error('Error loading sales products:', error);
        this.salesError = 'Failed to load products';
        this.salesLoading = false;
        this.salesProducts = this.getFallbackSalesProducts();
      }
    },
    
    async getBestsellersProducts(page = 1) {
      this.bestsellersLoading = true;
      this.bestsellersError = null;
      
      try {
        // Берём товары со 2-й страницы и далее, сортировка по убыванию цены — другой набор чем в Sales
        const apiPage = page + 1;
        const response = await this.$http.get(process.env.VUE_APP_API + `items?per_page=20&page=${apiPage}&sort=-price`);
        const allProducts = response.data.data || response.data.payload || response.data;
        
        this.bestsellersProducts = allProducts.slice(0, 5);
        
        if (this.bestsellersProducts.length < 5) {
          const fallback = await this.$http.get(process.env.VUE_APP_API + `items?per_page=20&page=1&sort=-price`);
          const moreProducts = fallback.data.data || fallback.data.payload || fallback.data;
          const needed = 5 - this.bestsellersProducts.length;
          const additional = moreProducts
            .filter(p => !this.bestsellersProducts.some(b => b.id === p.id))
            .slice(0, needed);
          this.bestsellersProducts = [...this.bestsellersProducts, ...additional];
        }
        
        this.bestsellersLoading = false;
      } catch (error) {
        console.error('Error loading bestsellers:', error);
        this.bestsellersError = 'Failed to load products';
        this.bestsellersLoading = false;
        this.bestsellersProducts = this.getFallbackBestsellersProducts();
      }
    },
    
    getFallbackSalesProducts() {
      return [
        { id: 1, name: "Spider Webs", price: 0.02, old_price: 0.04, img: "/images/item" },
        { id: 2, name: "Low Quality Valves", price: 0.04, old_price: 0.05, img: "/images/item1" },
        { id: 3, name: "Pistol Bullet", price: 0.08, old_price: 0.09, img: "/images/item3" },
        { id: 4, name: "Wooden Arrow", price: 0.09, old_price: 0.10, img: "/images/item" },
        { id: 5, name: "Medium Quality Valves", price: 0.11, old_price: 0.13, img: "/images/item1" }
      ];
    },
    getFallbackBestsellersProducts() {
      return [
        { id: 6, name: "Assault Rifle", price: 2.50, img: "/images/item2" },
        { id: 7, name: "Metal Chest Plate", price: 1.80, img: "/images/item" },
        { id: 8, name: "Rocket Launcher", price: 5.00, img: "/images/item1" },
        { id: 9, name: "C4 Explosive", price: 3.20, img: "/images/item3" },
        { id: 10, name: "Metal Fragments", price: 0.15, img: "/images/item" }
      ];
    },
    
    getProductImage(product) {
      console.log('Product image data:', product.img || product.image, 'imgDomain:', this.imgDomain);
      
      // Используем ту же логику что и в Products.vue
      if (this.imgDomain && (product.img || product.image)) {
        const imageName = product.img || product.image;
        return `${this.imgDomain}${imageName}.png`;
      }
      
      // Fallback к изображению по умолчанию
      console.log('Using fallback image for product:', product.name);
      return require('../assets/item.png');
    },
    
    handleImageError(event) {
      console.log('Image failed to load:', event.target.src);
      // Fallback к изображению по умолчанию если основное не загрузилось
      event.target.src = require('../assets/item.png');
    },
    
    openProductModal(product) {
      // Эмитим событие для открытия модального окна товара
      this.$emit('openProductModal', product);
    },
    
    initCarousel() {
      this.$nextTick(() => {
        const prevBtns = document.querySelectorAll('.carousel-nav.carousel-prev');
        const nextBtns = document.querySelectorAll('.carousel-nav.carousel-next');
        
        prevBtns.forEach(btn => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            const section = e.currentTarget.dataset.section;
            this.prevPage(section);
          });
        });
        
        nextBtns.forEach(btn => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            const section = e.currentTarget.dataset.section;
            this.nextPage(section);
          });
        });
      });
    },
    
    prevPage(section) {
      if (section === 'sales' && this.currentSalesPage > 1) {
        this.currentSalesPage--;
        this.getSalesProducts(this.currentSalesPage);
      } else if (section === 'bestsellers' && this.currentBestsellersPage > 1) {
        this.currentBestsellersPage--;
        this.getBestsellersProducts(this.currentBestsellersPage);
      }
    },
    
    nextPage(section) {
      if (section === 'sales') {
        this.currentSalesPage++;
        this.getSalesProducts(this.currentSalesPage);
      } else if (section === 'bestsellers') {
        this.currentBestsellersPage++;
        this.getBestsellersProducts(this.currentBestsellersPage);
      }
    },
    
    toggleCard(cardIndex) {
      // Если кликнули на уже активную карточку, закрываем её
      if (this.activeCard === cardIndex) {
        this.activeCard = null;
      } else {
        // Иначе открываем новую карточку
        this.activeCard = cardIndex;
      }
    }
  }
}
</script>